function getSubtleCrypto() {
  const cryptoObj = window.crypto || window.msCrypto;
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new Error('Web Crypto is not supported');
  }
  return cryptoObj.subtle;
}

function base64ToBytes(base64) {
  const normalized = String(base64 || '').replace(/\s/g, '');
  const binary = window.atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return window.btoa(binary);
}

function bytesToBase64Url(bytes) {
  return bytesToBase64(bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function bytesToText(bytes) {
  let text = '';
  for (let i = 0; i < bytes.length; i++) {
    text += String.fromCharCode(bytes[i]);
  }
  return text;
}

function stripLeadingZero(bytes) {
  let start = 0;
  while (start < bytes.length - 1 && bytes[start] === 0) {
    start++;
  }
  return bytes.slice(start);
}

function getPemBody(pem) {
  return String(pem || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line && !line.includes('BEGIN') && !line.includes('END') && !line.startsWith('Comment:'))
    .join('');
}

function readLength(bytes, offset) {
  let length = bytes[offset++];
  if (length < 0x80) {
    return { length, offset };
  }

  const lengthBytes = length & 0x7f;
  length = 0;
  for (let i = 0; i < lengthBytes; i++) {
    length = (length << 8) + bytes[offset++];
  }
  return { length, offset };
}

function readDerInteger(bytes, offset) {
  if (bytes[offset++] !== 0x02) {
    throw new Error('Invalid RSA private key');
  }

  const lengthInfo = readLength(bytes, offset);
  offset = lengthInfo.offset;
  const value = stripLeadingZero(bytes.slice(offset, offset + lengthInfo.length));
  return {
    value,
    offset: offset + lengthInfo.length,
  };
}

function readSshString(bytes, offset) {
  const length = (bytes[offset] << 24) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
  offset += 4;
  return {
    value: bytes.slice(offset, offset + length),
    offset: offset + length,
  };
}

function parseSshRsaPublicKey(publicKey) {
  const bytes = base64ToBytes(getPemBody(publicKey));
  let result = readSshString(bytes, 0);
  if (bytesToText(result.value) !== 'ssh-rsa') {
    throw new Error('Only ssh-rsa public keys are supported');
  }

  result = readSshString(bytes, result.offset);
  const e = stripLeadingZero(result.value);
  result = readSshString(bytes, result.offset);
  const n = stripLeadingZero(result.value);

  return {
    kty: 'RSA',
    n: bytesToBase64Url(n),
    e: bytesToBase64Url(e),
    ext: true,
  };
}

function parsePkcs1PrivateKey(privateKey) {
  const bytes = base64ToBytes(getPemBody(privateKey));
  let offset = 0;
  if (bytes[offset++] !== 0x30) {
    throw new Error('Invalid RSA private key');
  }

  offset = readLength(bytes, offset).offset;
  offset = readDerInteger(bytes, offset).offset;
  const nInfo = readDerInteger(bytes, offset);
  const eInfo = readDerInteger(bytes, nInfo.offset);
  const dInfo = readDerInteger(bytes, eInfo.offset);
  const pInfo = readDerInteger(bytes, dInfo.offset);
  const qInfo = readDerInteger(bytes, pInfo.offset);
  const dpInfo = readDerInteger(bytes, qInfo.offset);
  const dqInfo = readDerInteger(bytes, dpInfo.offset);
  const qiInfo = readDerInteger(bytes, dqInfo.offset);

  return {
    kty: 'RSA',
    n: bytesToBase64Url(nInfo.value),
    e: bytesToBase64Url(eInfo.value),
    d: bytesToBase64Url(dInfo.value),
    p: bytesToBase64Url(pInfo.value),
    q: bytesToBase64Url(qInfo.value),
    dp: bytesToBase64Url(dpInfo.value),
    dq: bytesToBase64Url(dqInfo.value),
    qi: bytesToBase64Url(qiInfo.value),
    ext: true,
  };
}

export function importSshRsaPublicKey(publicKey) {
  return getSubtleCrypto().importKey(
    'jwk',
    parseSshRsaPublicKey(publicKey),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
}

export function importPkcs1PrivateKey(privateKey) {
  return getSubtleCrypto().importKey(
    'jwk',
    parsePkcs1PrivateKey(privateKey),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['decrypt'],
  );
}

export async function encryptWithRsaPublicKey(value, publicKey) {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const encoded = new TextEncoder().encode(String(value));
  const encrypted = await getSubtleCrypto().encrypt({ name: 'RSA-OAEP' }, publicKey, encoded);
  return bytesToBase64(new Uint8Array(encrypted));
}

export async function decryptWithRsaPrivateKey(value, privateKey) {
  if (value === null || value === undefined || value === '') {
    return value;
  }

  const encrypted = base64ToBytes(value);
  const decrypted = await getSubtleCrypto().decrypt({ name: 'RSA-OAEP' }, privateKey, encrypted);
  return new TextDecoder().decode(decrypted);
}
